import { Banner, Checkbox, Typography } from '@weareopenr/component-library';
import { Autocomplete, TextField } from '@mui/material';
import { useMemo } from 'react';
import { UseFormReturn, useController, useWatch } from 'react-hook-form';
import { usePriceSchemes } from '../../../hooks/usePriceSchemes/usePriceSchemes';
import { IPriceScheme } from '../../../types/price.types';
import { IOptionGroupValues } from '../types';
import styles from '../CreateOptionGroupModal.module.scss';

interface IOptionGroupPriceSchemesProps {
  useFormReturn: UseFormReturn<IOptionGroupValues>;
}

export const OptionGroupPriceSchemes = ({ useFormReturn }: IOptionGroupPriceSchemesProps) => {
  const { data: priceSchemes, isLoading } = usePriceSchemes();
  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormReturn;

  const optionGroupName = useWatch({ control, name: 'name' });

  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'price_scheme_ids',
    rules: {
      required: 'Please select at least one price scheme',
      validate: (val) => (Array.isArray(val) && val.length > 0) || 'Please select at least one price scheme',
    },
  });

  const selectedPriceSchemes = useMemo(
    () =>
      value
        ? value
            .map((priceSchemeId: string) => priceSchemes?.find((scheme) => scheme.id === priceSchemeId))
            .filter((scheme): scheme is IPriceScheme => !!scheme)
        : [],
    [value, priceSchemes],
  );

  return (
    <>
      <Typography size="xxlarge" className={styles['heading-text']}>
        Define Price Schemes for <span className={styles['bold-text']}>{optionGroupName}</span>
      </Typography>
      <Typography>Select a price scheme to begin setting prices for this option group.</Typography>
      <section className={styles['section']}>
        <div className={styles['price-scheme-container']}>
          <Autocomplete
            loading={isLoading}
            isOptionEqualToValue={(option, value) => value?.id === option?.id}
            onChange={(e, data) => {
              const priceSchemeIds = data
                .filter((scheme): scheme is IPriceScheme => typeof scheme !== 'string')
                .map((scheme) => scheme.id);
              onChange(priceSchemeIds);
            }}
            renderInput={(params) => (
              <TextField
                label="Price Schemes"
                {...params}
                error={!!errors.price_scheme_ids}
                helperText={errors.price_scheme_ids?.message?.toString()}
              />
            )}
            options={priceSchemes || []}
            value={selectedPriceSchemes}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
            multiple
            onFocus={() => clearErrors('price_scheme_ids')}
            className={styles['autocomplete']}
            limitTags={2}
            renderOption={(props, option, { selected }) => {
              return (
                <li key={option.id} {...props}>
                  <Checkbox checked={selected} onChange={() => {}} />
                  {option.name}
                </li>
              );
            }}
          />
        </div>

        <Banner
          text={
            <>
              <strong>Quick note</strong> - All schemes used at publish require prices. You can add these via the Price
              Schemes page or directly via the option group details page
            </>
          }
        />
      </section>
    </>
  );
};
